import React from 'react';
import { faqData } from '../data/mock';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import useScrollAnimation from '../hooks/useScrollAnimation';

// Custom Accordion Trigger with Plus icon that rotates to X
const CustomAccordionTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-6 text-base font-medium text-[#333333] transition-all hover:text-[#5a7fa8] text-left group",
        className
      )}
      {...props}
    >
      <span className="pr-8">{children}</span>
      <div className="w-10 h-10 rounded-full border border-[#bcbbb4] flex items-center justify-center transition-all duration-500 group-hover:border-[#5a7fa8] group-hover:bg-[#5a7fa8]/5 group-data-[state=open]:border-[#c9a87c] group-data-[state=open]:bg-[#c9a87c] group-data-[state=open]:rotate-45">
        <Plus 
          size={16} 
          className="shrink-0 text-[#666666] transition-all duration-500 group-hover:text-[#5a7fa8] group-data-[state=open]:text-white" 
        />
      </div>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
CustomAccordionTrigger.displayName = "CustomAccordionTrigger";

export const FAQSection = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });
  const [accordionRef, accordionVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="faq" className="bg-[#fffef2] py-24 lg:py-32 relative">
      {/* Decorative elements */}
      <div className="absolute top-20 right-20 w-32 h-32 border border-[#e8e7da] rounded-full opacity-50" />
      <div className="absolute bottom-40 left-16 w-24 h-24 border border-[#e8e7da] rounded-full opacity-30" />
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left Column - Title */}
          <div
            ref={titleRef}
            className={`slide-in-left ${titleVisible ? 'is-visible' : ''}`}
          >
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#333333] leading-tight lg:sticky lg:top-32">
              {faqData.sectionTitle}
            </h2>
            
            {/* Decorative line */}
            <div className="mt-8 flex items-center gap-3">
              <div className="w-12 h-px bg-gradient-to-r from-[#c9a87c] to-transparent" />
              <div className="w-1.5 h-1.5 rounded-full bg-[#5a7fa8] opacity-60" />
            </div>
          </div>

          {/* Right Column - Accordion */}
          <div
            ref={accordionRef}
            className={`stagger-children ${accordionVisible ? 'is-visible' : ''}`}
          >
            <AccordionPrimitive.Root type="single" collapsible className="w-full">
              {faqData.questions.map((item, index) => (
                <AccordionPrimitive.Item
                  key={index}
                  value={`item-${index}`}
                  className="border-b border-[#e8e7da] faq-item rounded-sm transition-colors duration-300"
                >
                  <CustomAccordionTrigger>
                    {item.question}
                  </CustomAccordionTrigger>
                  <AccordionPrimitive.Content className="overflow-hidden text-base text-[#666666] leading-relaxed data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className="pb-6 pt-0 pr-12 pl-0">
                      {item.answer}
                    </div>
                  </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
              ))}
            </AccordionPrimitive.Root>
          </div>
        </div>
      </div>
    </section>
  );
};
